(() => {
  const slugify = (text) =>
    String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 72);

  const normalize = (text) =>
    String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const titleCase = (text) => {
    const value = String(text || '').trim();
    if (!value) return value;
    const letters = value.replace(/[^A-Za-z]/g, '');
    if (!letters || letters !== letters.toUpperCase() || letters.length < 6) return value;
    return value.toLowerCase().replace(/\b([a-z])/g, (match) => match.toUpperCase());
  };

  const tidyHeading = (text) => {
    let value = String(text || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    value = value.replace(/^(\d+(?:\.\d+)*)\.(\S)/, '$1. $2');
    return titleCase(value);
  };

  const isDuplicateTitle = (raw, pageTitle, index) => {
    const comparable = raw.replace(/[–—-]/g, ' ').toLowerCase().trim();
    const title = String(pageTitle || '').toLowerCase().trim();
    if (!comparable) return true;
    if (comparable === title) return true;
    if (title && comparable.indexOf(title) === 0 && comparable.length < title.length + 12) return true;

    const nodeNorm = normalize(raw);
    const titleNorm = normalize(pageTitle);
    if (!nodeNorm || !titleNorm) return false;
    if (nodeNorm === titleNorm || nodeNorm.startsWith(titleNorm) || titleNorm.startsWith(nodeNorm)) return true;
    if (nodeNorm.includes(titleNorm) && nodeNorm.length < titleNorm.length + 40) return true;
    if (index !== 0) return false;

    const titleWords = titleNorm.split(' ').filter((word) => word.length > 3);
    const overlap = titleWords.filter((word) => nodeNorm.includes(word)).length;
    return overlap >= 2 || (/(policy|warranty|terms|agreement)/.test(nodeNorm) && /impakto/.test(nodeNorm));
  };

  const isHeadingParagraph = (el) => {
    if (el.closest('li, blockquote, table, figcaption')) return false;
    const text = el.textContent.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    if (text.length < 3 || text.length > 90) return false;
    if (el.querySelector('a, img, ul, ol, table')) return false;
    if (/^\d+(?:\.\d+)*\.\s*\S/.test(text)) {
      const after = text.replace(/^\d+(?:\.\d+)*\.\s*/, '');
      return after.length > 1 && after.length < 80 && !/[.?!]\s/.test(after);
    }
    if (/:$/.test(text) && text.split(' ').length <= 8 && !/[.?!]/.test(text.slice(0, -1))) {
      return !/^(we|the|this|that|if|please|all|you|our|it|these|those|for|when|once|to)\b/i.test(text);
    }
    return false;
  };

  const sectionNodes = (body) => {
    const nodes = [];
    body.querySelectorAll('h2, h3, h4, p').forEach((el) => {
      if (el.matches('h2, h3, h4')) {
        nodes.push(el);
        return;
      }
      if (el.closest('li, blockquote, table')) return;

      const strong = el.querySelector('strong');
      if (strong && el.querySelectorAll('strong').length === 1) {
        const extra = el.textContent.replace(strong.textContent, '').trim();
        if (!extra) {
          const label = strong.textContent.trim();
          if (label.length >= 3 && label.length <= 90) {
            nodes.push(strong);
            return;
          }
        }
      }

      if (isHeadingParagraph(el)) nodes.push(el);
    });
    return nodes;
  };

  const uniqueId = (base, seen) => {
    let unique = base;
    let n = 2;
    while (seen.has(unique) || document.getElementById(unique)) {
      unique = base + '-' + n;
      n += 1;
    }
    seen.add(unique);
    return unique;
  };

  const init = () => {
    const page = document.querySelector('.gh-policy');
    const body = page && page.querySelector('.gh-policy__body');
    const nav = page && page.querySelector('.gh-policy__toc-nav');
    if (!page || !body || !nav) return;

    body.querySelectorAll('meta, title, style, script, link').forEach((el) => el.remove());

    const pageTitle = (page.querySelector('.gh-policy__title')?.textContent || '').trim();
    const seen = new Set();
    const items = [];

    sectionNodes(body).forEach((el, index) => {
      const raw = el.textContent.trim();
      if (!raw) return;
      if (isDuplicateTitle(raw, pageTitle, index)) {
        const wrap = el.closest('p') || el;
        if (wrap && wrap.parentElement && wrap !== body) wrap.hidden = true;
        return;
      }

      const pretty = tidyHeading(raw);
      el.textContent = pretty;
      if (/^\d+(?:\.\d+)*\./.test(pretty)) {
        el.innerHTML = pretty.replace(
          /^(\d+(?:\.\d+)*)\.\s*/,
          '<span class="gh-policy__num">$1.</span> '
        );
      }

      const id = uniqueId(slugify(pretty) || 'section', seen);
      const wrap = el.tagName === 'STRONG' ? el.closest('p') || el : el;
      if (wrap.tagName === 'P' || wrap.classList) wrap.classList.add('gh-policy__section-title');
      wrap.id = id;
      if (wrap !== el) el.removeAttribute('id');

      if (!/^\d+\.\d+/.test(pretty) && !(/:$/.test(pretty) && !/^\d+\./.test(pretty))) {
        items.push({ id, label: pretty.replace(/:$/, '') });
      }
    });

    const firstHeading = [...body.querySelectorAll('h2, h3, h4, .gh-policy__section-title')].find(
      (el) => !el.hidden && !el.closest('[hidden]')
    );
    if (firstHeading) firstHeading.classList.add('gh-policy__first-heading');

    const lede = [...body.children].find(
      (el) => el.tagName === 'P' && !el.hidden && !el.classList.contains('gh-policy__section-title')
    );
    if (lede) lede.classList.add('gh-policy__lede');

    if (!items.length) {
      page.classList.add('gh-policy--no-toc');
      return;
    }

    nav.innerHTML = items
      .map(
        (item, index) =>
          `<a href="#${item.id}" class="gh-policy__toc-link"${index === 0 ? ' aria-current="true"' : ''}>${item.label}</a>`
      )
      .join('');

    const links = [...nav.querySelectorAll('.gh-policy__toc-link')];
    const targets = items.map((item) => document.getElementById(item.id)).filter(Boolean);

    const setCurrent = (id) => {
      links.forEach((link) => {
        link.toggleAttribute('aria-current', link.getAttribute('href') === '#' + id);
      });
    };

    nav.addEventListener('click', (event) => {
      const link = event.target.closest('.gh-policy__toc-link');
      if (!link) return;
      const id = (link.getAttribute('href') || '').slice(1);
      const target = id && document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      setCurrent(id);
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', '#' + id);
    });

    if ('IntersectionObserver' in window && targets.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (visible && visible.target.id) setCurrent(visible.target.id);
        },
        { rootMargin: '-18% 0px -68% 0px', threshold: [0.1, 0.4, 1] }
      );
      targets.forEach((target) => observer.observe(target));
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
